// Report exporters (CSV / Excel / PDF) for the currently filtered+sorted
// list of properties. The report always starts with a "cover" block that
// prints board metadata, active filters and summary stats — then the rows.
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const fmtEuro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtInt = (n) => new Intl.NumberFormat("it-IT").format(n || 0);
const fmtPct = (v) => (v === null || v === undefined) ? "" : `${(v * 100).toFixed(1)}%`;
const yn = (v) => v === true || v === "SI" ? "Sì" : v === false || v === "NO" ? "No" : v === "NI" ? "NI" : "";

const nowStamp = () => {
  const d = new Date();
  const pad = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
};

const humanNow = () => new Date().toLocaleString("it-IT", { dateStyle: "long", timeStyle: "short" });

// Turn the filters object into human-readable "Label: value" lines. The
// same labels are shown in the sidebar so the reader can immediately see
// what the report is based on.
export const describeFilters = (filters = {}, sortConfig = []) => {
  const lines = [];
  if (filters.q) lines.push(["Ricerca", `"${filters.q}"`]);
  if (Array.isArray(filters.macro_area) && filters.macro_area.length) {
    lines.push(["Macro area", filters.macro_area.join(", ")]);
  }
  if (filters.citta) lines.push(["Città", filters.citta]);
  if (filters.mq_min || filters.mq_max) lines.push(["MQ", `${filters.mq_min || "…"} – ${filters.mq_max || "…"} m²`]);
  if (filters.prezzo_min || filters.prezzo_max) lines.push(["Prezzo", `${filters.prezzo_min ? fmtEuro(filters.prezzo_min) : "…"} – ${filters.prezzo_max ? fmtEuro(filters.prezzo_max) : "…"}`]);
  if (filters.eur_mq_min || filters.eur_mq_max) lines.push(["€/m²", `${filters.eur_mq_min ? fmtEuro(filters.eur_mq_min) : "…"} – ${filters.eur_mq_max ? fmtEuro(filters.eur_mq_max) : "…"}`]);
  if (filters.da_ristrutturare) {
    const map = { SI: "Sì", NO: "No", NI: "Non indicato" };
    lines.push(["Ristrutturato", map[filters.da_ristrutturare] || filters.da_ristrutturare]);
  }
  if (filters.ascensore === "true") lines.push(["Ascensore", "Sì"]);
  if (filters.ascensore === "false") lines.push(["Ascensore", "No"]);
  if (filters.tipo_venditore) lines.push(["Venditore", filters.tipo_venditore === "PRIVATO" ? "Privato" : "Agenzia"]);
  if (Array.isArray(filters.scost_bands) && filters.scost_bands.length) lines.push(["Fasce scostamento", filters.scost_bands.join(", ")]);
  if (filters.favorites_only) lines.push(["Solo preferiti", "Sì"]);
  if (filters.only_duplicates) lines.push(["Solo duplicati", "Sì"]);
  if (sortConfig && sortConfig.length) {
    const labels = { citta: "Città", macro_area: "Macro area", indirizzo: "Indirizzo", piano: "Piano", mq: "MQ", prezzo: "Prezzo", eur_mq: "€/MQ", da_ristrutturare: "Ristrutturato", ascensore: "Ascensore", venditore: "Venditore", scostamento: "Scostam.", note: "Note" };
    lines.push(["Ordinamento", sortConfig.map((s, i) => `${i + 1}. ${labels[s.key] || s.key} ${s.direction === "asc" ? "↑" : "↓"}`).join(" · ")]);
  }
  if (lines.length === 0) lines.push(["Filtri", "Nessuno (dataset completo)"]);
  return lines;
};

// Common row builder — one row per property, plain values for CSV/XLSX
// and array-of-cells for PDF.
const ROW_HEADERS = ["Città", "Macro area", "Indirizzo", "N. civico", "Piano", "MQ", "Prezzo (€)", "€/MQ", "Ristrutturato", "Ascensore", "Venditore", "Scostamento", "Preferito", "Duplicato", "Note", "Link"];

const buildRows = (rows, duplicates) => rows.map((p) => {
  const dup = duplicates && duplicates.get ? duplicates.get(p.id) : null;
  return {
    citta: p.citta || "",
    macro_area: p.macro_area || "",
    indirizzo: p.indirizzo || "",
    civico: p.civico || "",
    piano: p.piano || "",
    mq: p.mq || 0,
    prezzo: p.prezzo || 0,
    eur_mq: p.eur_mq || 0,
    ristrutturato: yn(p.da_ristrutturare),
    ascensore: p.ascensore ? "Sì" : "No",
    venditore: p.tipo_venditore === "PRIVATO" ? "Privato" : (p.agenzia_nome || "Agenzia"),
    scostamento: p.scostamento === null || p.scostamento === undefined ? "" : Number(p.scostamento),
    preferito: p.is_favorite ? "Sì" : "",
    duplicato: dup ? (dup.level === "red" ? "Certo" : "Possibile") : "",
    note: (p.note || "").replace(/\s+/g, " ").trim(),
    link: p.link || "",
  };
});

// ---------------- CSV ----------------
export const exportCSV = ({ rows, duplicates, filters, sortConfig, stats, boardName }) => {
  const body = buildRows(rows, duplicates);
  const filterLines = describeFilters(filters, sortConfig);
  const lines = [];
  lines.push(`"ImmoMap — Report ${boardName || "scheda"}"`);
  lines.push(`"Esportato il ${humanNow()}"`);
  lines.push("");
  lines.push('"Filtri attivi"');
  for (const [k, v] of filterLines) lines.push(`"${k}","${String(v).replace(/"/g, '""')}"`);
  lines.push("");
  lines.push('"Statistiche"');
  lines.push(`"Immobili filtrati","${stats?.count ?? body.length}"`);
  lines.push(`"Prezzo medio","${fmtEuro(stats?.avg_prezzo)}"`);
  lines.push(`"€/m² medio","${fmtEuro(stats?.avg_eur_mq)}"`);
  lines.push(`"Valore totale","${fmtEuro(stats?.total_prezzo)}"`);
  lines.push("");
  lines.push(ROW_HEADERS.map((h) => `"${h}"`).join(","));
  for (const r of body) {
    const cells = [
      r.citta, r.macro_area, r.indirizzo, r.civico, r.piano,
      r.mq, r.prezzo, Math.round(r.eur_mq),
      r.ristrutturato, r.ascensore, r.venditore,
      r.scostamento === "" ? "" : fmtPct(r.scostamento),
      r.preferito, r.duplicato, r.note, r.link,
    ];
    lines.push(cells.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","));
  }
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  triggerDownload(blob, `immomap-${slug(boardName)}-${nowStamp()}.csv`);
};

// ---------------- XLSX ----------------
export const exportXLSX = ({ rows, duplicates, filters, sortConfig, stats, boardName }) => {
  const body = buildRows(rows, duplicates);
  const wb = XLSX.utils.book_new();
  // Cover sheet — title, filters, stats
  const cover = [];
  cover.push([`ImmoMap — Report ${boardName || "scheda"}`]);
  cover.push([`Esportato il ${humanNow()}`]);
  cover.push([]);
  cover.push(["Filtri attivi"]);
  for (const [k, v] of describeFilters(filters, sortConfig)) cover.push([k, v]);
  cover.push([]);
  cover.push(["Statistiche"]);
  cover.push(["Immobili filtrati", stats?.count ?? body.length]);
  cover.push(["Prezzo medio", stats?.avg_prezzo || 0]);
  cover.push(["€/m² medio", stats?.avg_eur_mq || 0]);
  cover.push(["Valore totale", stats?.total_prezzo || 0]);
  const coverSheet = XLSX.utils.aoa_to_sheet(cover);
  coverSheet["!cols"] = [{ wch: 26 }, { wch: 60 }];
  XLSX.utils.book_append_sheet(wb, coverSheet, "Report");

  // Data sheet
  const data = [ROW_HEADERS];
  for (const r of body) data.push([
    r.citta, r.macro_area, r.indirizzo, r.civico, r.piano,
    r.mq, r.prezzo, Math.round(r.eur_mq),
    r.ristrutturato, r.ascensore, r.venditore,
    r.scostamento === "" ? "" : Number((r.scostamento * 100).toFixed(2)) / 100,
    r.preferito, r.duplicato, r.note, r.link,
  ]);
  const dataSheet = XLSX.utils.aoa_to_sheet(data);
  dataSheet["!cols"] = [
    { wch: 14 }, { wch: 22 }, { wch: 30 }, { wch: 10 }, { wch: 10 },
    { wch: 8 }, { wch: 14 }, { wch: 10 }, { wch: 14 },
    { wch: 10 }, { wch: 22 }, { wch: 12 }, { wch: 10 },
    { wch: 12 }, { wch: 40 }, { wch: 40 },
  ];
  XLSX.utils.book_append_sheet(wb, dataSheet, "Immobili");
  XLSX.writeFile(wb, `immomap-${slug(boardName)}-${nowStamp()}.xlsx`);
};

// ---------------- PDF (landscape, big header) ----------------
export const exportPDF = ({ rows, duplicates, filters, sortConfig, stats, boardName }) => {
  const body = buildRows(rows, duplicates);
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const w = doc.internal.pageSize.getWidth();
  const marginX = 32;

  // Header band
  doc.setFillColor(0, 122, 255);
  doc.rect(0, 0, w, 68, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("ImmoMap", marginX, 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("REAL ESTATE MAPPING", marginX, 50);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(boardName || "Scheda", w - marginX, 34, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Esportato il ${humanNow()}`, w - marginX, 50, { align: "right" });

  // Filter recap (2 columns)
  const filterLines = describeFilters(filters, sortConfig);
  doc.setTextColor(60, 60, 60);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("FILTRI ATTIVI", marginX, 92);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const colW = (w - marginX * 2) / 2;
  const rowsPerCol = Math.ceil(filterLines.length / 2);
  filterLines.forEach(([k, v], i) => {
    const col = Math.floor(i / rowsPerCol);
    const row = i % rowsPerCol;
    const x = marginX + col * colW;
    const y = 106 + row * 12;
    doc.setFont("helvetica", "bold");
    doc.text(`${k}:`, x, y);
    doc.setFont("helvetica", "normal");
    // Truncate long values so they never overflow into the next column
    const maxChars = Math.floor(colW / 4.2);
    const val = String(v).length > maxChars ? String(v).slice(0, maxChars - 1) + "…" : String(v);
    doc.text(val, x + 78, y);
  });

  // Stats strip
  const statsY = 118 + rowsPerCol * 12;
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(marginX, statsY, w - marginX * 2, 44, 6, 6, "F");
  doc.setTextColor(30, 30, 30);
  const statItems = [
    ["IMMOBILI", `${fmtInt(stats?.count ?? body.length)}`],
    ["PREZZO MEDIO", fmtEuro(stats?.avg_prezzo)],
    ["€/m² MEDIO", fmtEuro(stats?.avg_eur_mq)],
    ["VALORE TOTALE", fmtEuro(stats?.total_prezzo)],
  ];
  const statW = (w - marginX * 2) / statItems.length;
  statItems.forEach(([label, val], i) => {
    const x = marginX + statW * i + 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(label, x, statsY + 16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(20, 20, 20);
    doc.text(val, x, statsY + 34);
  });

  // Data table
  autoTable(doc, {
    startY: statsY + 60,
    head: [ROW_HEADERS.filter((_, i) => i !== 15)], // drop Link column in PDF (too long)
    body: body.map((r) => [
      r.citta, r.macro_area, r.indirizzo, r.civico, r.piano,
      fmtInt(r.mq), fmtEuro(r.prezzo), fmtEuro(Math.round(r.eur_mq)),
      r.ristrutturato, r.ascensore, r.venditore,
      r.scostamento === "" ? "" : fmtPct(r.scostamento),
      r.preferito, r.duplicato,
      (r.note || "").slice(0, 80),
    ]),
    styles: { fontSize: 7.5, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fillColor: [0, 122, 255], textColor: 255, fontStyle: "bold", fontSize: 8 },
    alternateRowStyles: { fillColor: [250, 251, 253] },
    columnStyles: {
      2: { cellWidth: 100 }, // indirizzo
      3: { cellWidth: 40 },  // civico
      14: { cellWidth: 100 }, // note
    },
    margin: { left: marginX, right: marginX },
    didDrawPage: (data) => {
      // Page footer with numbering
      const pg = doc.internal.getNumberOfPages();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Pagina ${data.pageNumber} di ${pg}`, w - marginX, doc.internal.pageSize.getHeight() - 14, { align: "right" });
      doc.text("ImmoMap · Report scheda", marginX, doc.internal.pageSize.getHeight() - 14);
    },
  });

  doc.save(`immomap-${slug(boardName)}-${nowStamp()}.pdf`);
};

const slug = (s = "") => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "scheda";
const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
};
